import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IRootState, IRootThunkDispatch } from "../../redux/store";
import { OpenLoadingBarThunk } from "../../redux/common/isLoading/thunk";
import {
  axiosAPIWithJson,
  axiosGet,
  axiosPostWithFormData,
} from "../../util/axios";
import {
  OpenSnackBarWithResultFail,
  OpenSnackBarWithResultSuccess,
} from "../../redux/common/snackBar/thunk";

import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Button, Grid } from "@mui/material";
import { EditNote } from "@mui/icons-material";
import {
  handleClearCleaningRouteListThunk,
  initCleaningDetailRouteThunk,
  initCleaningRouteListThunk,
} from "../../redux/cleaningRoute/thunk";
import FormDialog from "../../component/common/FormDialog";
import EditCleaningRoute from "../../component/cleaning/EditCleaningRoute";
import { CleaningRouteList } from "../../redux/cleaningRoute/state";
import RouteExcel from "../../component/route/RouteExcel";
import { ClearRouteFormDataThunk } from "../../redux/route/thunk";
import { ButtonTextGrid } from "../..";

function CleaningRoute() {
  const dispatch = useDispatch<IRootThunkDispatch>();
  const dataFetchedRef = useRef(false);

  const cleaningRouteState = useSelector(
    (state: IRootState) => state.cleaningRouteState
  );

  const {
    cleaningRouteList,
    selectedDetailRouteList,
    route_name,
    effective_date,
    weekday,
    mon,
    tue,
    wed,
    thu,
    fri,
    sat,
    sun,
    holiday,
    cleaner_id,
    remark,
    route_cleaning_id,
  } = cleaningRouteState;

  const [openEditCleaningRouteDialog, setOpenEditCleaningRouteDialog] =
    useState<boolean>(false);

  const [openAddCleaningRouteDialog, setOpenAddCleaningRouteDialog] =
    useState<boolean>(false);

  const [openExcelRouteDialog, setOpenExcelRouteDialog] =
    useState<boolean>(false);

  const [passCheck, setPassCheck] = useState<boolean>(false);

  const [excelFile, setExcelFile] = useState<File[] | null>(null);

  const [failedRowList, setFailedRowList] = useState<Record<string, any>>({});

  const [passCheckMessage, setPassCheckMessage] = useState<string>("");

  const [rowListCount, setRowListCount] = useState<Record<string, any>>({});

  const [rowLength, setRowLength] = useState<number>(0);

  useEffect(() => {
    const controller = new AbortController();
    if (dataFetchedRef.current) return;
    dataFetchedRef.current = true;
    getCleaningRouteData();
    return () => {
      controller.abort();
    };
  }, []);

  const columns: GridColDef[] = [
    {
      field: "route_cleaning_id",
      headerName: "清潔編號",
      flex: 1,
    },
    {
      field: "route_name",
      headerName: "清潔路線名稱",
      flex: 1,
    },
    { field: "effective_date", headerName: "生效日期", flex: 1 },
    { field: "create_date", headerName: "創建時間", flex: 1 },
    { field: "username", headerName: "負責司機", flex: 1 },
    {
      field: "action",
      headerName: "清潔詳情",
      flex: 1,
      renderCell: (data) => (
        <div>
          <Button
            variant="contained"
            onClick={() => handleOpenEditCleaningRouteDialog(data.row)}
          >
            <ButtonTextGrid>
              <EditNote />
              <Grid>詳情</Grid>
            </ButtonTextGrid>
          </Button>
        </div>
      ),
    },
  ];

  async function handleOpenEditCleaningRouteDialog(
    cleaningRoute: CleaningRouteList,
  ) {
    setOpenEditCleaningRouteDialog(true);
    dispatch(OpenLoadingBarThunk(true));
    const result = await axiosGet(
      `/api/cleaning/cleaningRouteDetailCms?route_cleaning_id=${cleaningRoute.route_cleaning_id}`,
    );
    dispatch(OpenLoadingBarThunk(false));
    if (result.data.success) {
      dispatch(
        initCleaningDetailRouteThunk(
          result.data.cleaningRouteDetail,
          cleaningRoute,
        ),
      );
    } else {
      dispatch(OpenSnackBarWithResultFail("未能取得清潔路線資料"));
    }
  }

  function handlecloseEditCleaningRouteDialog() {
    dispatch(handleClearCleaningRouteListThunk());

    setOpenEditCleaningRouteDialog(false);
    setOpenAddCleaningRouteDialog(false);
  }

  async function getCleaningRouteData() {
    dispatch(OpenLoadingBarThunk(true));
    const result = await axiosGet("/api/cleaning/cleaningRouteCms");
    dispatch(OpenLoadingBarThunk(false));
    if (result.data.success) {
      dispatch(initCleaningRouteListThunk(result.data.cleaningRouteList));
    } else {
      dispatch(OpenSnackBarWithResultFail("未能取得清潔路線資料"));
    }
  }

  function hasHolidayFlag(route: { holiday?: number | null }): boolean {
    return route?.holiday === 1;
  }

  async function handleSubmitCleaningRoute() {
    let result;

    if (!route_name) {
      dispatch(OpenSnackBarWithResultFail("你未有填寫車線名稱"));
      return;
    }
    if (!cleaner_id) {
      dispatch(OpenSnackBarWithResultFail("你未有填寫清潔人員"));
      return;
    }
    const hasWeekday = [mon, tue, wed, thu, fri, sat, sun].some((d) => d === 1);
    if (!hasWeekday) {
      dispatch(OpenSnackBarWithResultFail("你未有填寫工作日"));
      return;
    }
    if (!selectedDetailRouteList) {
      dispatch(OpenSnackBarWithResultFail("你未有選擇桶名稱"));
      return;
    }

    const sameCleanerRoutes =
      cleaningRouteList?.filter(
        (route) =>
          route.user_id === cleaner_id &&
          !(route_cleaning_id && route.route_cleaning_id === route_cleaning_id),
      ) ?? [];

    const weekdayKeys = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
    const weekdayFlags = { mon, tue, wed, thu, fri, sat, sun };

    const hasWeekdayConflict = sameCleanerRoutes.some((route) =>
      weekdayKeys.some((day) => weekdayFlags[day] === 1 && (route[day] ?? 0) === 1),
    );

    if (hasWeekdayConflict) {
      dispatch(
        OpenSnackBarWithResultFail(
          "此清潔人員在選擇的工作日已存在清潔車線，不能重複設定",
        ),
      );
      return;
    }

    const currentHolidaySuitable = hasHolidayFlag(cleaningRouteState);

    if (currentHolidaySuitable) {
      const hasHolidayConflict = sameCleanerRoutes.some((route) =>
        hasHolidayFlag(route),
      );

      if (hasHolidayConflict) {
        dispatch(
          OpenSnackBarWithResultFail(
            "此清潔人員已有一條假日適用清潔車線，不能再新增／設定另一條",
          ),
        );
        return;
      }
    }

    const form = {
      route_name,
      cleaner_id,
      selectedDetailRouteList,
      remark,
      effective_date,
      weekday,
      mon: mon ?? 0,
      tue: tue ?? 0,
      wed: wed ?? 0,
      thu: thu ?? 0,
      fri: fri ?? 0,
      sat: sat ?? 0,
      sun: sun ?? 0,
      holiday: holiday ?? 0,
    };

    dispatch(OpenLoadingBarThunk(true));

    if (route_cleaning_id) {
      result = await axiosAPIWithJson.put(
        `api/cleaning/routes/${route_cleaning_id}`,
        form,
      );
      if (result.data.success) {
        dispatch(OpenLoadingBarThunk(false));
        dispatch(OpenSnackBarWithResultSuccess(result.data.message));
      } else {
        dispatch(OpenLoadingBarThunk(false));
        dispatch(OpenSnackBarWithResultFail("未能成功修改車線"));
      }
    } else {
      result = await axiosAPIWithJson.post("api/cleaning/routes", form);
      if (result.data.success) {
        dispatch(OpenLoadingBarThunk(false));
        dispatch(OpenSnackBarWithResultSuccess(result.data.message));
      } else {
        dispatch(OpenLoadingBarThunk(false));
        dispatch(OpenSnackBarWithResultFail("未能成功新增車線"));
      }
    }
    dispatch(handleClearCleaningRouteListThunk());
    getCleaningRouteData();
    setOpenEditCleaningRouteDialog(false);
    setOpenAddCleaningRouteDialog(false);
  }

  function handleAddCleaningRoute() {
    setOpenAddCleaningRouteDialog(true);
  }

  function handleExcelRoute() {
    dispatch(ClearRouteFormDataThunk());
    setOpenExcelRouteDialog(true);
  }

  function handleCloseExcelRouteDialog() {
    setOpenExcelRouteDialog(false);
    setPassCheck(false);
    setFailedRowList({});
    setRowListCount({});
    setRowLength(0);
    setPassCheckMessage("");
  }

  async function handleSubmitExcelRoute() {
    if (!excelFile) {
      dispatch(OpenSnackBarWithResultFail("你未有上傳excel"));
      return;
    }
    const formData = new FormData();
    formData.append("route_excel", excelFile[0]);
    let result;
    try {
      dispatch(OpenLoadingBarThunk(true));
      if (passCheck) {
        result = await axiosPostWithFormData(
          "/api/routes/upload/cleaning",
          formData,
        );

        if (result.data.success) {
          dispatch(OpenSnackBarWithResultSuccess("你已成功上載清潔路線"));
        } else {
          dispatch(OpenSnackBarWithResultFail(result.data.message));
        }
        handleCloseExcelRouteDialog();
      } else {
        result = await axiosPostWithFormData("/api/routes/check", formData);
        setPassCheckMessage(result.data.message);
        if (result.data.success) {
          setPassCheck(true);
          setRowListCount(result.data.data);
          dispatch(OpenSnackBarWithResultSuccess(result.data.message));
        } else {
          dispatch(OpenSnackBarWithResultFail(result.data.message));
          if (result.data.data) {
            setFailedRowList(result.data.data);
          }
        }
        if (result.data.length) {
          setRowLength(result.data.length);
        }
        dispatch(OpenLoadingBarThunk(false));
      }
      dispatch(OpenLoadingBarThunk(false));
    } catch (err) {
      dispatch(OpenLoadingBarThunk(false));
      dispatch(OpenSnackBarWithResultFail("網路錯誤"));
    }
  }

  return (
    <div>
      <Button variant="contained" onClick={handleAddCleaningRoute}>
        <ButtonTextGrid>新增清潔車線</ButtonTextGrid>
      </Button>

      <Button
        variant="contained"
        onClick={handleExcelRoute}
        style={{ marginLeft: "10px" }}
      >
        <ButtonTextGrid>上傳清潔車線excel</ButtonTextGrid>
      </Button>

      <div style={{ height: "900px", width: "100%" }}>
        <DataGrid
          rows={cleaningRouteList || []}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 30 },
            },
          }}
          pageSizeOptions={[5, 10]}
          checkboxSelection
          getRowId={(row) => row.route_cleaning_id}
        />
      </div>

      <FormDialog
        title="更改清潔車線"
        open={openEditCleaningRouteDialog}
        handleClose={handlecloseEditCleaningRouteDialog}
        handleSubmit={handleSubmitCleaningRoute}
        size="lg"
      >
        <EditCleaningRoute />
      </FormDialog>

      <FormDialog
        title="新增清潔車線"
        open={openAddCleaningRouteDialog}
        handleClose={handlecloseEditCleaningRouteDialog}
        handleSubmit={handleSubmitCleaningRoute}
        size="lg"
      >
        <EditCleaningRoute />
      </FormDialog>

      <FormDialog
        title="上傳清潔車線excel"
        open={openExcelRouteDialog}
        handleClose={handleCloseExcelRouteDialog}
        handleSubmit={handleSubmitExcelRoute}
        buttonName={passCheck ? "上傳" : "檢查"}
        size="lg"
      >
        <RouteExcel
          onChangeFunc={(file: File[]) => {
            setExcelFile(file);
            setPassCheck(false);
            setFailedRowList({});
            setPassCheckMessage("");
            setRowLength(0);
          }}
          passCheckMessage={passCheckMessage}
          failedRowList={failedRowList}
          rowListCount={rowListCount}
          rowLength={rowLength}
        />
      </FormDialog>
    </div>
  );
}

export default CleaningRoute;
